/**
 * MED-1319 Booking links for one group.
 *
 * A coach copies a link and sends it to participants. A participant opens the
 * link and books a 1:1 session without a login.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  CopyButton,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  AlertCircle,
  Check,
  Copy,
  ExternalLink,
  Link2,
  RefreshCw,
} from 'react-feather';
import {
  coachPortalApi,
  type CoachBookingLink,
  type CoachBookingLinksResponse,
} from '../../../services/api/coachPortal';
import { schedulingTokens as tokens } from '../../../components/scheduling/schedulingTokens';
import classes from './GroupBookingLinks.module.css';

const PROFILE_PATH = '/coach/profile';

function bookingUrl(token: string): string {
  return `${window.location.origin}/book/${token}`;
}

function unavailableMessage(link: CoachBookingLink): string {
  if (link.unavailable_reason === 'event_type_not_mapped') {
    const duration = link.duration_minutes
      ? `${link.duration_minutes} min `
      : '';
    return `Participants cannot book yet. Pick a Calendly event type for ${duration}sessions in the Calendly card on your profile.`;
  }
  if (link.unavailable_reason === 'event_type_unusable') {
    return 'Participants cannot book. The Calendly event type for this link is turned off, deleted, or a team event. Pick an active personal event type in the Calendly card on your profile.';
  }
  return 'Participants cannot book yet. Connect Calendly in the Calendly card on your profile.';
}

/** A status pill as in docs/STYLE_GUIDE.md: Complete style, or Draft style. */
function LinkStatusPill({ ready }: { ready: boolean }) {
  return (
    <Text
      span
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
        backgroundColor: ready ? tokens.success : tokens.borderLight,
        color: ready ? tokens.surface : tokens.textSecondary,
      }}
    >
      {ready ? 'Ready' : 'Not bookable yet'}
    </Text>
  );
}

interface BookingLinkRowProps {
  groupId: string;
  link: CoachBookingLink;
  /** True when the modal already tells the coach to connect Calendly. */
  calendlyNoticeShown: boolean;
  /** The API revokes the old row and returns a new row with a new id. */
  onRegenerated: (oldId: string, link: CoachBookingLink) => void;
  /** Fetch the list again, for example when this link changed elsewhere. */
  onReload: () => void;
}

function BookingLinkRow({
  groupId,
  link,
  calendlyNoticeShown,
  onRegenerated,
  onReload,
}: BookingLinkRowProps) {
  const [confirming, setConfirming] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const regenerateRef = useRef<HTMLButtonElement>(null);
  // The confirm box replaces the Regenerate button, so move focus into it,
  // and back to Regenerate when the box closes.
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (confirming) cancelRef.current?.focus();
    else if (wasConfirming.current) regenerateRef.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const url = bookingUrl(link.token);
  const showUnavailable =
    !link.available &&
    !(
      calendlyNoticeShown &&
      link.unavailable_reason === 'calendly_not_connected'
    );

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const result = await coachPortalApi.regenerateGroupBookingLink(
        groupId,
        link.id
      );
      onRegenerated(link.id, result.link);
      setConfirming(false);
      notifications.show({
        title: 'New link ready',
        message: `The old ${link.touchpoint_label} link no longer works.`,
        color: 'medleyGreen.6',
      });
    } catch (err) {
      // A 404 means this link row is already gone, for example because it was
      // regenerated in another tab. Show the current links.
      const status = (err as { status?: number }).status;
      if (status === 404) {
        onReload();
        notifications.show({
          title: 'This link changed',
          message: 'We reloaded your links.',
          color: 'warningAmber.5',
        });
      } else {
        notifications.show({
          title: 'Could not make a new link',
          message: 'Your link did not change. Please try again.',
          color: 'dangerRed.5',
        });
      }
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
            <Text fw={600} size="sm">
              {link.touchpoint_label}
            </Text>
            <LinkStatusPill ready={link.available} />
          </Group>
          <Group gap={4} wrap="nowrap">
            <CopyButton value={url}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy link'} withArrow>
                  <ActionIcon
                    variant={
                      copied ? 'filled' : link.available ? 'light' : 'subtle'
                    }
                    color={
                      copied
                        ? 'medleyGreen.6'
                        : link.available
                          ? 'medleyGreen'
                          : 'gray'
                    }
                    size="lg"
                    aria-label={`Copy ${link.touchpoint_label} link`}
                    // The green check and the "Copied" tooltip confirm the
                    // copy. A toast too would stack over the modal.
                    onClick={copy}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
            <Tooltip label="Open in a new tab" withArrow>
              <ActionIcon
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                variant="subtle"
                color="gray"
                size="lg"
                aria-label={`Open ${link.touchpoint_label} link in a new tab`}
              >
                <ExternalLink size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        <Text
          size="xs"
          c="dimmed"
          ff="monospace"
          style={{ wordBreak: 'break-all' }}
        >
          {url}
        </Text>

        {showUnavailable && (
          <Alert
            color="warningAmber.5"
            variant="light"
            icon={<AlertCircle size={16} />}
            p="xs"
          >
            <Text size="xs">
              {unavailableMessage(link)}{' '}
              <Anchor component={Link} to={PROFILE_PATH} size="xs" fw={600}>
                Go to your profile
              </Anchor>
            </Text>
          </Alert>
        )}

        {confirming ? (
          <Box
            p="sm"
            style={{
              backgroundColor: tokens.dangerTint,
              borderRadius: 6,
              border: `1px solid ${tokens.dangerBorder}`,
            }}
          >
            <Text size="xs" mb="xs">
              The current link stops working at once. Anyone who has it must get
              the new link from you.
            </Text>
            <Group gap="xs" justify="flex-end">
              <Button
                ref={cancelRef}
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => setConfirming(false)}
                disabled={isRegenerating}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                color="dangerRed.5"
                loading={isRegenerating}
                onClick={handleRegenerate}
              >
                Make a new link
              </Button>
            </Group>
          </Box>
        ) : (
          <Group justify="flex-end">
            <Button
              ref={regenerateRef}
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<RefreshCw size={12} />}
              onClick={() => setConfirming(true)}
              aria-label={`Regenerate ${link.touchpoint_label} link`}
            >
              Regenerate
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

interface GroupBookingLinksProps {
  groupId: string;
  groupName: string;
  /** Button text. Name the group when one section shows a button per group. */
  label?: string;
  /**
   * 'oneOnOne' when the 1:1 Coaching section opens the window. That section
   * shows no groups, so the copy speaks of 1:1 participants instead.
   */
  context?: 'group' | 'oneOnOne';
  /** Show the group name under the window title. */
  showGroupName?: boolean;
}

/** A header button that opens the group's booking links in a modal. */
export function GroupBookingLinks({
  groupId,
  groupName,
  label = 'Booking links',
  context = 'group',
  showGroupName = true,
}: GroupBookingLinksProps) {
  const [opened, setOpened] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [data, setData] = useState<CoachBookingLinksResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      setData(await coachPortalApi.getGroupBookingLinks(groupId));
    } catch {
      // Do not show the links from an earlier load next to the error.
      setData(null);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [groupId]);

  // Fetch the links only when the modal opens.
  useEffect(() => {
    if (opened) load();
  }, [opened, load]);

  const handleRegenerated = (oldId: string, updated: CoachBookingLink) => {
    setData(prev =>
      prev
        ? {
            ...prev,
            links: prev.links.map(l => (l.id === oldId ? updated : l)),
          }
        : prev
    );
  };

  return (
    // The group header toggles the accordion on click. React sends clicks from
    // the modal portal up through this box, so stop them here.
    <Box maw="100%" onClick={e => e.stopPropagation()}>
      <Button
        variant="light"
        color="medleyGreen"
        size={isMobile ? 'sm' : 'xs'}
        radius="xl"
        className={classes.trigger}
        leftSection={<Link2 size={14} />}
        onClick={() => setOpened(true)}
        // A long group name truncates instead of running past the header.
        maw="100%"
        title={label}
        styles={{
          inner: { minWidth: 0 },
          label: { minWidth: 0, overflow: 'hidden' },
        }}
      >
        {/* Mantine's label is a flex box, which ignores text-overflow, so the
            ellipsis sits on an inner span. */}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      </Button>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={
          <Box>
            <Text fw={700} size="lg">
              Booking links
            </Text>
            {showGroupName && (
              <Text size="sm" c="dimmed">
                {groupName}
              </Text>
            )}
          </Box>
        }
        size="lg"
        radius="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {context === 'oneOnOne'
              ? 'Send a link to your 1:1 participants.'
              : 'Send a link to participants in this group.'}{' '}
            They pick an open time and book without a login.
          </Text>

          {isLoading && !data && (
            <Group justify="center" py="xl">
              <Loader
                size="sm"
                color="medleyGreen"
                aria-label="Loading booking links"
              />
            </Group>
          )}

          {loadError && (
            <Alert
              color="dangerRed.5"
              variant="light"
              icon={<AlertCircle size={16} />}
            >
              <Text size="sm">We could not load the booking links.</Text>
              <Anchor
                component="button"
                type="button"
                size="sm"
                fw={600}
                c="dangerRed.6"
                mt="xs"
                display="block"
                onClick={load}
              >
                Try again
              </Anchor>
            </Alert>
          )}

          {data && !data.calendly_connected && (
            <Alert
              color="warningAmber.5"
              variant="light"
              icon={<AlertCircle size={16} />}
            >
              <Text size="sm">
                Connect Calendly to turn on booking links. Participants cannot
                book until you do.
              </Text>
              <Anchor
                component={Link}
                to={PROFILE_PATH}
                size="sm"
                fw={600}
                c="medleyGreen.8"
                mt="xs"
                display="inline-block"
              >
                Go to your profile
              </Anchor>
            </Alert>
          )}

          {/* A fallback: the card hides this button when the cohort has no
            bookable touchpoint, but an older API does not send that flag. */}
          {data && data.links.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="md">
              This cohort has no 1:1 calls to book.
            </Text>
          )}

          {/* The key stays the same across a regenerate, which gives the
            row a new id. The row stays mounted, so the focus stays in it. */}
          {data?.links.map(link => (
            <BookingLinkRow
              key={`${link.touchpoint}:${link.duration_minutes ?? 0}`}
              groupId={groupId}
              link={link}
              calendlyNoticeShown={!data.calendly_connected}
              onRegenerated={handleRegenerated}
              onReload={load}
            />
          ))}
        </Stack>
      </Modal>
    </Box>
  );
}
